// The send pipeline shared by the reply composer and the compose dialog:
// upload what has to be uploaded, rewrite pasted images to their public URLs,
// normalise the HTML, derive the plain-text part.
//
// It used to be written out twice, inside each component's mutation, reading
// the component's state. That tied a send to a mounted composer. This version
// works from an `OutboundSnapshot` taken when Send is clicked, so it can run
// from the outbox long after the composer is gone (see lib/send-outbox.ts).
//
// Pure apart from the injected `upload`, which keeps it testable without
// Supabase and keeps this file free of any import that touches the network.

import type { OutboundAttachment } from './api';
import { htmlToPlain, prepareOutboundHtml } from './html-normalize';
import { findInlineImageIds, rewriteInlineImageSrcs } from './inline-images';
import type { AfterSend } from './macros';

export interface OutboundFile {
  /** The attachment chip's local id (also names the Storage object). */
  localId: string;
  file: File;
}

export interface OutboundSnapshot {
  /** Storage folder: the ticket id for a reply, `compose-<uuid>` for a new ticket. */
  bucket: string;
  /** The editor's HTML at the moment Send was clicked. */
  bodyHtml: string;
  /** Paperclip / drag-dropped files. */
  files: OutboundFile[];
  /** Pasted images, by the `data-pa-attach-id` in `bodyHtml`. Taken from the
   *  editor's registry at click time. */
  inlineImages: Map<string, File>;
  /** What is already uploaded: `localId` → URL for attachments, `inline:<id>`
   *  → URL for pasted images. Filled in as uploads finish and kept across
   *  attempts, so a retry never uploads a file twice. */
  uploaded: Map<string, string>;
}

export interface PreparedOutbound {
  /** text/plain part and `ticket_messages.body`. */
  body: string;
  /** Rich body with inline images pointing at Storage. */
  body_html: string;
  /** Attached files first, then pasted images — the order the workflow got
   *  them in before this was extracted. */
  attachments: OutboundAttachment[];
}

/** Where an upload run is: `done` finished so far, `total` that need
 *  uploading in this run (files already uploaded by an earlier attempt are not
 *  counted). */
export interface UploadProgress {
  done: number;
  total: number;
}

export interface OutboundDeps {
  /** Upload one file to `path` and return its public URL. Throw on failure. */
  upload: (file: File, path: string) => Promise<string>;
  /** Called just before each upload starts. Never called when nothing needs
   *  uploading. Lets the UI say "Uploading 2 of 3 files…". */
  onProgress?: (progress: UploadProgress) => void;
}

const safeName = (name: string): string => name.replace(/[^a-zA-Z0-9._-]/g, '_');

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const describe = (file: File, url: string): OutboundAttachment => ({
  filename: file.name,
  contentType: file.type || 'application/octet-stream',
  size_bytes: file.size,
  url,
});

export async function prepareOutbound(
  snap: OutboundSnapshot,
  deps: OutboundDeps,
): Promise<PreparedOutbound> {
  // How many uploads this run will actually make, for the progress label. The
  // loops below skip anything already in `snap.uploaded` (a retry), and a
  // pasted image only counts if its File is still there.
  const inlineIds = findInlineImageIds(snap.bodyHtml);
  const total =
    snap.files.filter((f) => !snap.uploaded.has(f.localId)).length +
    inlineIds.filter((id) => snap.inlineImages.has(id) && !snap.uploaded.has(`inline:${id}`))
      .length;
  let done = 0;
  const startUpload = () => deps.onProgress?.({ done, total });

  // Step 1: files attached with the paperclip. Done inside the send so a
  // failure is one retryable error rather than a silently broken attachment.
  const attached: OutboundAttachment[] = [];
  for (const item of snap.files) {
    let url = snap.uploaded.get(item.localId);
    if (!url) {
      const path = `${snap.bucket}/${item.localId}-${safeName(item.file.name)}`;
      startUpload();
      try {
        url = await deps.upload(item.file, path);
      } catch (e) {
        throw new Error(`Upload failed for ${item.file.name}: ${messageOf(e)}`);
      }
      snap.uploaded.set(item.localId, url);
      done += 1;
    }
    attached.push(describe(item.file, url));
  }

  // Step 2: images pasted into the body. Each is uploaded and its blob: src
  // rewritten to the public URL, so the recipient sees it inline and there is
  // an audit copy in Storage.
  const inline: OutboundAttachment[] = [];
  const idToUrl = new Map<string, string>();
  for (const id of inlineIds) {
    const file = snap.inlineImages.get(id);
    if (!file) continue; // leftover marker; caught by the blob: check below
    const cacheKey = `inline:${id}`;
    let url = snap.uploaded.get(cacheKey);
    if (!url) {
      const path = `${snap.bucket}/inline-${id}-${safeName(file.name)}`;
      startUpload();
      try {
        url = await deps.upload(file, path);
      } catch (e) {
        throw new Error(`Inline image upload failed: ${messageOf(e)}`);
      }
      snap.uploaded.set(cacheKey, url);
      done += 1;
    }
    idToUrl.set(id, url);
    inline.push(describe(file, url));
  }
  let html = rewriteInlineImageSrcs(snap.bodyHtml, idToUrl);

  // A pasted screenshot lives as a `blob:` URL in the editor while its File
  // sits in the editor's registry. Autosaved drafts persist the HTML but not
  // the registry, so after a refresh the blob is dead and there is nothing to
  // substitute — the reply used to ship with a broken image and no error.
  // Fail loudly instead; the draft is preserved so the image can be re-pasted.
  if (/src=["']blob:/i.test(html)) {
    throw new Error(
      'An image in this reply expired when the page reloaded. Delete it and paste it again before sending.',
    );
  }

  // Carry the composer's spacing with the message (blank rows stay blank rows,
  // every <p> is margin:0). See lib/html-normalize.ts.
  html = prepareOutboundHtml(html);

  return {
    body: htmlToPlain(html),
    body_html: html,
    attachments: [...attached, ...inline],
  };
}

// ── What the outbox carries ────────────────────────────────────────────────
// Everything a send needs, captured when Send is clicked. Nothing here points
// at component state, so the job can run, be retried, or be handed back to a
// composer after the original one has unmounted.

export type ReplyMode = 'reply' | 'reply_all' | 'forward';

export interface ReplyOutboxPayload {
  ticketId: string;
  /** Ticket subject, for toasts and the pending bubble. */
  ticketSubject: string | null;
  /** Minted per click; reused by every retry so Gmail is never hit twice. */
  idempotency_key: string;
  mode: ReplyMode;
  to: string[];
  cc: string[];
  bcc: string[];
  /** Forward only. */
  subject?: string;
  snapshot: OutboundSnapshot;
  /** A macro from the template this reply was built from: run after the reply has been sent
   *  (never on Undo or a failed send). See after-send.ts. */
  afterSend?: AfterSend;
  /** The sender's slack_user_id, for the macro's optimistic update. */
  actorSlackId?: string | null;
}

export interface ComposeOutboxPayload {
  idempotency_key: string;
  inbox_id?: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  priority: 'P1' | 'P2' | 'P3' | 'P4';
  category: string;
  snapshot: OutboundSnapshot;
}
